import type { TimelineItem } from './timeline'

/**
 * Card status determines the overall color/urgency of the pet card
 */
export type CardStatus = 'overdue' | 'urgent' | 'stable'

// 2 hours in milliseconds
const URGENT_WINDOW_MS = 2 * 60 * 60 * 1000

/**
 * Determine the card status based on timeline items
 *
 * Priority:
 * 1. Overdue - any dose past scheduled time
 * 2. Urgent - any dose within 2 hours
 * 3. Stable - no urgent actions
 */
export function getCardStatus(items: TimelineItem[]): CardStatus {
  const now = new Date()
  const twoHoursFromNow = new Date(now.getTime() + URGENT_WINDOW_MS)

  // Check doses only (appointments don't create urgency)
  const doses = items.filter((item) => item.type === 'dose')

  // Check for overdue doses
  const hasOverdue = doses.some((item) => item.time < now)
  if (hasOverdue) {
    return 'overdue'
  }

  // Check for urgent doses (within 2 hours)
  const hasUrgent = doses.some(
    (item) => item.time >= now && item.time <= twoHoursFromNow
  )
  if (hasUrgent) {
    return 'urgent'
  }

  return 'stable'
}

/**
 * Get the CSS class modifier for a card status
 */
export function getCardStatusClass(status: CardStatus): string {
  return `pet-card-v2--${status}`
}

/**
 * Get the next action preview text for the card header
 * e.g., "Siguiente: Amoxicilina 8:00"
 */
export function getNextActionPreview(items: TimelineItem[]): string | null {
  if (items.length === 0) return null

  const item = items[0]!

  if (item.type === 'dose') {
    const time = item.time.toLocaleTimeString('es', {
      hour: '2-digit',
      minute: '2-digit',
    })
    return `Siguiente: ${item.data.medicationNombre} ${time}`
  }

  if (item.type === 'appointment') {
    const time = item.time.toLocaleTimeString('es', {
      hour: '2-digit',
      minute: '2-digit',
    })
    return `Siguiente: ${item.data.motivo} ${time}`
  }

  return null
}
